import { useState, useEffect } from 'react';
import { useSystemStripeMode } from '@/hooks/useSystemStripeMode';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { WebhookConfiguration } from '@/components/WebhookConfiguration';
import { Beaker, Rocket, AlertTriangle, Check, CreditCard, Calendar, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

export default function PaymentGateway() {
  const { stripeMode, isTestMode, isProductionMode, updateMode, isUpdating, stripeModeChangedAt, changedByName, isLoading } = useSystemStripeMode();
  const { isSuperAdmin } = useUserProfile();
  
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMode, setPendingMode] = useState<'test' | 'production' | null>(null);

  useEffect(() => {
    document.title = 'Dashi | Gateway Pagamento';
  }, []);

  const handleModeChange = async (newMode: 'test' | 'production') => {
    if (newMode === stripeMode) {
      return;
    }

    if (newMode === 'production') {
      setPendingMode(newMode);
      setShowConfirmDialog(true);
    } else {
      await updateMode(newMode);
    }
  };

  const confirmModeChange = async () => {
    if (pendingMode) {
      await updateMode(pendingMode);
      setShowConfirmDialog(false);
      setPendingMode(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Acesso Negado
            </CardTitle>
            <CardDescription>
              Apenas Super Administradores podem acessar o Gateway de Pagamento
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const testCards = [
    { number: '4242 4242 4242 4242', type: 'Visa', description: 'Pagamento bem-sucedido' },
    { number: '4000 0025 0000 3155', type: 'Visa', description: 'Requer autenticação 3D Secure' },
    { number: '4000 0000 0000 9995', type: 'Visa', description: 'Pagamento recusado' },
  ];

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
      <h1 className="text-3xl font-bold flex items-center gap-2">
        <CreditCard className="h-8 w-8" />
        Gateway Pagamento
      </h1>
          <p className="text-muted-foreground mt-1">
            Configure o modo de operação do Stripe para seu sistema
          </p>
        </div>
      </div>
        {/* Status Atual */}
        <Card>
        <CardHeader>
          <CardTitle>Status Atual do Gateway</CardTitle>
          <CardDescription>
            Informações sobre o modo ativo de pagamentos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
            <div className="flex items-center gap-3">
              {isTestMode ? (
                <Beaker className="h-6 w-6 text-orange-500" />
              ) : (
                <Rocket className="h-6 w-6 text-green-500" />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">
                    {isTestMode ? 'Modo Teste' : 'Modo Produção'}
                  </span>
                  <Badge variant={isTestMode ? 'secondary' : 'default'}>
                    {isTestMode ? 'AMBIENTE DE TESTE' : 'AMBIENTE DE PRODUÇÃO'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {isTestMode 
                    ? 'Usando chaves de teste do Stripe - nenhuma cobrança real será processada'
                    : 'Usando chaves de produção - cobranças reais estão ativas'
                  }
                </p>
              </div>
            </div>
          </div>

          {stripeModeChangedAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>
                Última mudança: {format(new Date(stripeModeChangedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                {changedByName && ` pelo usuário ${changedByName}`}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seleção de Modo */}
      <Card>
        <CardHeader>
          <CardTitle>Alternar Modo de Operação</CardTitle>
          <CardDescription>
            Escolha entre ambiente de teste ou produção
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card className={cn(
              "relative transition-all duration-300",
              isTestMode && "ring-2 ring-primary shadow-lg"
            )}>
              {isTestMode && (
                <div className="absolute -top-3 left-4 px-3 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                  Modo Atual
                </div>
              )}
              <CardHeader>
                <CardTitle>Modo Teste</CardTitle>
                <CardDescription>
                  Use chaves de teste do Stripe para desenvolvimento
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Configure a chave de teste (sk_test_...) no secret <code className="bg-muted px-1 rounded">STRIPE_SECRET_KEY_TEST</code> nas configurações do backend.
                </p>
                <Button
                  onClick={() => handleModeChange('test')}
                  disabled={isTestMode || isUpdating}
                  className="w-full"
                >
                  {isTestMode ? 'Modo Ativo' : 'Ativar Modo Teste'}
                </Button>
              </CardContent>
            </Card>

            <Card className={cn(
              "relative transition-all duration-300",
              isProductionMode && "ring-2 ring-primary shadow-lg"
            )}>
              {isProductionMode && (
                <div className="absolute -top-3 left-4 px-3 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                  Modo Atual
                </div>
              )}
              <CardHeader>
                <CardTitle>Modo Produção</CardTitle>
                <CardDescription>
                  Use chaves reais do Stripe para transações de produção
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <strong>Atenção:</strong> O modo produção processa pagamentos reais.
                  </AlertDescription>
                </Alert>
                <p className="text-sm text-muted-foreground">
                  Configure a chave de produção (sk_live_...) no secret STRIPE_SECRET_KEY nas configurações do backend.
                </p>
                <Button
                  onClick={() => handleModeChange('production')}
                  disabled={isProductionMode || isUpdating}
                  variant={isProductionMode ? "default" : "destructive"}
                  className="w-full"
                >
                  {isProductionMode ? 'Modo Ativo' : 'Ativar Modo Produção'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

          {/* Instruções de Configuração */}
          <Card>
            <CardHeader>
              <CardTitle>Como Configurar as Chaves Stripe</CardTitle>
              <CardDescription>
                Configure as duas secrets no backend para cada ambiente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>Sistema com Secrets Separadas:</strong> Configure duas chaves diferentes para máxima segurança. 
                  O sistema escolherá automaticamente a chave correta baseado no modo ativo.
                </AlertDescription>
              </Alert>

              <ol className="list-decimal list-inside space-y-3 text-sm">
                <li>
                  <strong>Obtenha suas chaves do Stripe:</strong>
                  <ul className="list-disc list-inside ml-6 mt-2 text-muted-foreground space-y-1">
                    <li>Acesse o <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Dashboard (Teste)</a></li>
                    <li>Copie a chave de teste: <code className="bg-muted px-1 py-0.5 rounded">sk_test_...</code></li>
                    <li>Acesse o <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Dashboard (Produção)</a></li>
                    <li>Copie a chave de produção: <code className="bg-muted px-1 py-0.5 rounded">sk_live_...</code></li>
                  </ul>
                </li>
                <li>
                  <strong>Configure DUAS secrets no backend:</strong>
                  <ul className="list-disc list-inside ml-6 mt-2 text-muted-foreground space-y-1">
                    <li>Clique no botão abaixo para abrir as configurações de secrets</li>
                    <li>Configure <code className="bg-muted px-1 py-0.5 rounded">STRIPE_SECRET_KEY_TEST</code> com a chave <code className="bg-muted px-1 py-0.5 rounded">sk_test_...</code></li>
                    <li>Configure <code className="bg-muted px-1 py-0.5 rounded">STRIPE_SECRET_KEY</code> com a chave <code className="bg-muted px-1 py-0.5 rounded">sk_live_...</code></li>
                    <li>Salve as alterações</li>
                  </ul>
                </li>
                <li>
                  <strong>Ative o modo desejado:</strong>
                  <ul className="list-disc list-inside ml-6 mt-2 text-muted-foreground space-y-1">
                    <li>Retorne a esta página</li>
                    <li>Clique em "Ativar Modo Teste" ou "Ativar Modo Produção"</li>
                    <li>O sistema usará automaticamente a secret correta e validará o formato</li>
                  </ul>
                </li>
              </ol>

              <div className="pt-4">
              <Button variant="default" onClick={() => window.open('', '_blank')}>
                Acessar Configurações de Secrets
              </Button>
              </div>

              <Alert>
                <Check className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>Vantagens:</strong> Com duas secrets separadas, você nunca precisará trocar chaves manualmente. 
                  Basta alternar o modo e o sistema usa a chave apropriada automaticamente.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

      {/* Cartões de Teste (apenas no modo teste) */}
      {isTestMode && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Cartões de Teste do Stripe
            </CardTitle>
            <CardDescription>
              Use estes cartões para testar pagamentos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {testCards.map((card, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-mono font-semibold">{card.number}</p>
                      <p className="text-xs text-muted-foreground">{card.description}</p>
                    </div>
                  </div>
                  <Badge variant="outline">{card.type}</Badge>
                </div>
              ))}
              <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>Dica:</strong> Use qualquer data futura para validade e qualquer CVV de 3 dígitos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Webhook Configuration */}
      <WebhookConfiguration />

      {/* Informações de Segurança */}
      <Card className="border-yellow-200 dark:border-yellow-900 bg-yellow-50 dark:bg-yellow-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-yellow-900 dark:text-yellow-100">
            <AlertTriangle className="h-5 w-5" />
            Segurança e Boas Práticas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-yellow-900 dark:text-yellow-100">
          <div className="flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>Sempre teste completamente em modo teste antes de ativar produção</p>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>As mudanças de modo são registradas com data e usuário responsável</p>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>Apenas Super Administradores podem alterar o modo de operação</p>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Confirmação para Produção */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Ativar Modo Produção?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="font-semibold text-foreground">
                Você está prestes a ativar o modo PRODUÇÃO do Stripe.
              </p>
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <p className="text-sm text-foreground">
                  <strong className="text-destructive">ATENÇÃO:</strong> Esta ação irá:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• Processar cobranças reais de clientes</li>
                  <li>• Usar suas chaves de API de produção</li>
                  <li>• Gerar transações financeiras reais</li>
                </ul>
              </div>
              <p className="text-sm">
                Certifique-se de que você testou completamente todos os fluxos em modo teste antes de prosseguir.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmModeChange}
              disabled={isUpdating}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isUpdating ? 'Ativando...' : 'Sim, Ativar Produção'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
